import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { Z_INDEX } from '@constants/z-index';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { errorIsNetwork } from '@lib/utils/errorIsNetwork';
import { showToast } from '@lib/utils/toastmaster';
import { formatDurationToString } from '@lib/utils/units';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal, Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { billingStore } from '@stores/Billing';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import css from './MediaDeleteModal.module.scss';

export type MediaDeleteModalRef = {
  open: () => void;
  close: () => void;
};

/** Lightweight descriptor for items the modal is about to delete. Callers
 * sourcing from the library folder map full Media objects into this shape;
 * the upload status panel synthesizes these from the notification's titles
 * and mediaIds alone. */
export interface MediaDeleteItem {
  id: string;
  name: string;
  /** Backend video duration in seconds (for storage-reclamation display).
   * 0 / undefined is fine — the modal falls back to 0 ("0s"). */
  durationSeconds?: number;
}

/** Adapter for callers that already have full Media objects. */
export function mediaToDeleteItems(media: Media | Media[]): MediaDeleteItem[] {
  const list = Array.isArray(media) ? media : [media];
  return list.map((m) => ({
    id: m._id,
    name: m.metadata?.name || 'Unknown',
    durationSeconds: m.metadata?.video?.duration,
  }));
}

/**
 * Presentation-only delete confirmation dialog. All data dependencies —
 * library identity, library name, and the actual delete operation — are
 * injected via props so the modal can be mounted from the library page
 * (where LibraryFolderProvider is available) as well as from surfaces
 * without folder context (the upload status panel, for example).
 *
 * The `onConfirmDelete` callback performs the delete and is responsible
 * for any store mutation + refresh chore. The modal handles open/close,
 * tracking events, toast on success/failure.
 */
export const MediaDeleteModal = observer(
  forwardRef<
    MediaDeleteModalRef,
    {
      items: MediaDeleteItem[];
      libraryId: string;
      libraryName: string;
      onConfirmDelete: (mediaIds: string[]) => Promise<void>;
      onOpen?: () => void;
      onClose?: () => void;
      onDeleted?: () => void;
    }
  >(
    (
      {
        items,
        libraryId,
        libraryName,
        onConfirmDelete,
        onOpen: onOpenProp,
        onClose: onCloseProp,
        onDeleted,
      },
      ref,
    ) => {
      const [open, setOpen] = useState(false);
      const [isDeleting, setIsDeleting] = useState(false);
      const isMobile = useIsMobile();

      const isBulk = items.length > 1;
      const selectedCount = items.length;
      const firstMediaName = items[0]?.name || 'Unknown';
      const otherMediaNamesTooltip = (
        <>
          {items.slice(1).map((m, i) => (
            <div key={i} className={css.tooltipItem}>
              {m.name}
            </div>
          ))}
        </>
      );

      // Storage reclamation uses backend video duration; 0 when unavailable
      // (e.g. still processing, or caller synthesized a minimal item with
      // no duration). Images (frameCount=1) intentionally still contribute
      // their ~0.03s frame duration here — the backend's storage quota is
      // measured in duration-equivalent, so deleting an image does reclaim
      // that fractional amount.
      const totalStorageGainedDuration = items.reduce((total, m) => {
        return total + (m.durationSeconds || 0);
      }, 0);

      const formattedStorageGainedDuration = formatDurationToString(totalStorageGainedDuration, {
        precision: 0,
        detail: 2,
      });

      const expectedStorageAfterDeletion = billingStore.storageLeft + totalStorageGainedDuration;
      const formattedStorageAfterDeletion = formatDurationToString(expectedStorageAfterDeletion, {
        precision: 0,
        detail: 2,
      });

      const mediaIds = items.map((m) => m.id);

      // Declarative, because hosts unmount this while it is still open: DeleteBatchModalHost
      // removes the notification that keeps it mounted from inside its own confirm handler. The
      // old register-in-onOpen pattern leaked a stack entry there, which pinned `hasOpenModals`
      // true and silently disabled drag-drop upload app-wide until reload.
      useModalRegistration('media-delete', open);

      const onOpen = () => {
        setOpen(true);
        trackingStore.mediaDeleteModalOpened({
          libraryId,
          mediaIds,
          count: selectedCount,
          totalDurationSeconds: totalStorageGainedDuration,
        });
        onOpenProp?.();
      };
      const onClose = () => {
        setOpen(false);
        onCloseProp?.();
      };

      useImperativeHandle(ref, () => ({
        open: onOpen,
        close: onClose,
      }));

      /**
       * Performs the delete + fires the appropriate success / error toast.
       * Lifted out of `handleConfirmDelete` so the error toast's `onRetry`
       * callback can re-invoke the same logic from the notif-bell's
       * "Retry all" affordance. `closeOnSuccess` is true for the initial
       * confirm (close the modal after the deletion settles) and false
       * for retry attempts (the modal might already be gone; if it's
       * still open, the user can close it themselves).
       */
      const attemptDelete = async (closeOnSuccess: boolean): Promise<void> => {
        const filenames = items.map((it) => it.name);
        try {
          await onConfirmDelete(mediaIds);

          // Single-media deletes get the friendly filename-bearing template;
          // bulk deletes use the count + library + storage-freed summary,
          // with the full filename list attached as bell-card "More info"
          // details. Both end up routed into the notif-bell + visual toast.
          if (selectedCount === 1) {
            showToast('media-delete-success-single', {
              filename: firstMediaName,
              libraryName,
            });
          } else {
            showToast('media-delete-success', {
              libraryName,
              count: selectedCount,
              storageFreed: formattedStorageGainedDuration,
              filenames,
            });
          }

          if (closeOnSuccess) {
            onDeleted?.();
            onClose();
          }
        } catch (error) {
          console.error('Delete failed:', error);
          const isNetworkError = errorIsNetwork(error);
          if (selectedCount === 1) {
            showToast('media-delete-error-single', {
              filename: firstMediaName,
              libraryName,
              isNetworkError,
            });
          } else {
            showToast('media-delete-error', {
              libraryName,
              count: selectedCount,
              filenames,
              isNetworkError,
              // Pipe a retry callback through so the bell's "More info"
              // modal can offer "Retry all" without re-opening the delete
              // dialog. Recursive — failed retries fire a new toast with
              // its own retry handler, ad infinitum.
              //
              // Known race: this modal stays open after a failure (per the
              // UX — see the "keep modal open on error" decision). If the
              // user clicks Delete here AND "Delete all" in the bell's
              // More-info modal back-to-back, both fire `onConfirmDelete`
              // in parallel; the second sees 404s on the same mediaIds and
              // surfaces another error toast. Not data-corrupting (delete
              // is idempotent at the row level — 404 just means "already
              // gone"), just noisy. Accepted; not worth a debounce yet.
              onRetry: () => attemptDelete(false),
            });
          }
        }
      };

      const handleConfirmDelete = async () => {
        if (isDeleting || items.length === 0) return;
        setIsDeleting(true);
        try {
          await attemptDelete(true);
        } finally {
          setIsDeleting(false);
        }
      };

      // Shared inner content for both the desktop modal and the mobile drawer.
      const body = (
        <>
          <IconButton className={css.closeButton} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
          <h2>Delete media</h2>
          <div className={css.content}>
            <p>
              Are you sure you want to delete <b>{firstMediaName}</b>
              {isBulk && (
                <>
                  {' '}
                  and{' '}
                  <Tooltip title={otherMediaNamesTooltip} classes={{ popper: css.tooltipPopper }}>
                    <span className={css.otherMediaNames}>
                      <b>{selectedCount - 1} other media</b>
                    </span>
                  </Tooltip>
                </>
              )}
              ?
            </p>
            <p className={css.warning}>This action is irreversible!</p>
            <p>This operation will:</p>
            <ul className={css.operationList}>
              <li>
                Free up <b>{formattedStorageGainedDuration}</b> worth of storage
              </li>
              <li>
                <b>Not</b> reimburse the credits used to process this media
              </li>
            </ul>
          </div>
          <div className={css.content}>
            <p>
              After deletion, you will have <b>{formattedStorageAfterDeletion}</b> worth of storage
              left.
            </p>
          </div>
          <div className={classNames(css.actionButtons, isDeleting && css.deleting)}>
            <ActionButton
              variant="secondary"
              onClick={() => {
                trackingStore.mediaDeleteCancelled({ libraryId, mediaIds, count: selectedCount });
                onClose();
              }}
              type="button"
            >
              Cancel
            </ActionButton>
            <ActionButton variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : `Delete${isBulk ? ` ${selectedCount} media` : ''}`}
            </ActionButton>
          </div>
        </>
      );

      // Mobile: a bottom drawer (stacks above the notifications/uploads drawer
      // when opened from there). Desktop: the centered modal.
      if (isMobile) {
        return (
          <MobileDrawer
            open={open}
            onClose={onClose}
            onOpen={() => {}}
            frosted
            // Stacks above the notifications/uploads drawer (1400), below tooltips (1500).
            zIndex={Z_INDEX.aboveMobileDrawer}
          >
            <div className={css.drawerBody}>{body}</div>
          </MobileDrawer>
        );
      }

      return (
        <Modal
          open={open}
          onClose={onClose}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            {body}
          </div>
        </Modal>
      );
    },
  ),
);
